import CallButtonIcon from '@mui/icons-material/Call'
import CallEndButtonIcon from '@mui/icons-material/CallEnd'
import Close from '@mui/icons-material/Close'
import Dialpad from '@mui/icons-material/Dialpad'
import FiberManualRecord from '@mui/icons-material/FiberManualRecord'
import Stop from '@mui/icons-material/Stop'
import Alert from '@mui/material/Alert'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import NativeSelect from '@mui/material/NativeSelect'
import Paper, { type PaperProps } from '@mui/material/Paper'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import Draggable from 'react-draggable'

import Button from '@/app/component/atom/button'
import ControlIconButton from '@/app/component/atom/icon-button'
import { DEFAULT_SENDER_ID_VALUE } from '@/app/definitions'

export type CallerIdType = {
  value: string
  label: string
}

export enum CallStateType {
  Default = 'DEFAULT', // Initial render state
  Idle = 'IDLE',
  Initialized = 'INITIALIZED',
  Ringing = 'RINGING',
  Incall = 'INCALL',
  HangingUp = 'HANGING_UP',
  CallEnded = 'CALL_ENDED',
}

const callStateMap: { [key: string]: string } = {
  IDLE: 'Ready',
  INITIALIZED: 'Calling...',
  RINGING: 'Calling...',
  INCALL: 'In call',
  HANGING_UP: 'Hanging up...',
  CALL_ENDED: 'Call ended',
}

const unavailTooltip = 'Available once the call connects'

const keypadKeys = [
  ['1', ''],
  ['2', 'ABC'],
  ['3', 'DEF'],
  ['4', 'GHI'],
  ['5', 'JKL'],
  ['6', 'MNO'],
  ['7', 'PQRS'],
  ['8', 'TUV'],
  ['9', 'WXYZ'],
  ['*', ''],
  ['0', ''],
  ['#', ''],
]

type Props = {
  callerIds: CallerIdType[]
  callState: CallStateType
  canCall: boolean
  canEnd: boolean
  canRecord: boolean
  canSendDtmf: boolean
  connectionState: 'connecting' | 'connected'
  contactName: string
  errorMessage?: string
  recording: boolean
  selectedCallerId: CallerIdType

  onCall: () => void
  onCallerIdChange: (callerId: CallerIdType) => void
  onClose: () => void
  onDismissError: () => void
  onHangup: () => void
  onRecordToggle: () => void
  onSendDtmf: (digit: string) => void
}

const CallDialogUI: React.FC<Props> = (props) => {
  const {
    callerIds = [],
    callState,
    canCall,
    canEnd,
    canRecord,
    canSendDtmf,
    connectionState,
    contactName,
    errorMessage,
    recording,
    selectedCallerId,
    onCall,
    onCallerIdChange,
    onClose,
    onDismissError,
    onHangup,
    onRecordToggle,
    onSendDtmf,
  } = props

  const [keypadOpen, setKeypadOpen] = React.useState(false)
  const [keysPressed, setKeysPressed] = React.useState('')
  const keysPressedRef = React.useRef<HTMLOutputElement>(null)
  const isCallInProgress = [
    CallStateType.Initialized,
    CallStateType.Ringing,
    CallStateType.Incall,
    CallStateType.HangingUp,
  ].includes(callState)
  const callButtonTitle = getCallButtonTitle(callState)

  React.useEffect(() => {
    if (!canSendDtmf) {
      setKeypadOpen(false)
      setKeysPressed('')
    }
  }, [canSendDtmf])

  React.useEffect(() => {
    const display = keysPressedRef.current
    if (display && keypadOpen && keysPressed) {
      display.scrollLeft = display.scrollWidth
    }
  }, [keypadOpen, keysPressed])

  const handleKeyPress = (digit: string) => {
    if (!canSendDtmf) {
      return
    }

    setKeysPressed((previous) => previous + digit)
    onSendDtmf(digit)
  }

  const handleCallerIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === DEFAULT_SENDER_ID_VALUE) {
      onCallerIdChange({
        label: 'System Default',
        value: '',
      })
    } else {
      const foundCallerId = callerIds.find((cid) => cid.value === e.target.value)
      if (foundCallerId) {
        onCallerIdChange(foundCallerId)
      }
    }
  }

  return (
    <FloatingCallBox>
      <StyledDialogTitle id="call-dialog-title">
        <StyledShortenedLabel title={`Call ${contactName}`} variant="subtitle1" variantMapping={{ subtitle1: 'span' }}>
          Call {contactName}
        </StyledShortenedLabel>
        <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="caption">
          {connectionState === 'connected' ? 'Connected' : 'Connecting'}
        </Typography>
        <ControlIconButton aria-label="Close call dialog" size="small" onClick={onClose}>
          <Close fontSize="small" />
        </ControlIconButton>
      </StyledDialogTitle>
      <DialogContent>
        <audio id="audio" autoPlay={true} style={{ display: 'none' }}></audio>
        {errorMessage && (
          <Alert severity="error" onClose={onDismissError} sx={{ marginTop: 1 }}>
            {errorMessage}
          </Alert>
        )}
        <CallStatus>
          <Typography id="call-dialog-call-state" variant="h6">
            {callStateMap[callState]}
          </Typography>
          {recording && (
            <RecordingIndicator>
              <FiberManualRecord sx={{ fontSize: 10 }} />
              <Typography variant="caption">REC</Typography>
            </RecordingIndicator>
          )}
        </CallStatus>
        <div
          style={{
            padding: '15px 0px',
          }}
        >
          <FormControl variant="outlined" fullWidth size="small">
            <NativeSelect
              id="select-caller-id"
              disabled={callState !== CallStateType.Idle}
              fullWidth
              value={selectedCallerId?.value ?? DEFAULT_SENDER_ID_VALUE}
              onChange={handleCallerIdChange}
            >
              <option key="system-default" value="-1">
                System Default
              </option>
              {callerIds.map(({ value: callerIdPhonenumber, label }: CallerIdType) => (
                <option key={`${label}-${callerIdPhonenumber}`} value={callerIdPhonenumber}>
                  {label}
                </option>
              ))}
            </NativeSelect>
          </FormControl>
        </div>
        <div id="call-dialog-keypad" aria-label="Call keypad" hidden={!canSendDtmf || !keypadOpen} role="group">
          <KeypadPanel>
            <Typography color="text.secondary" variant="caption">
              Keys pressed
            </Typography>
            <KeypadDisplay ref={keysPressedRef} aria-label="Keys pressed" aria-live="off" tabIndex={0}>
              {keysPressed || '\u00a0'}
            </KeypadDisplay>
            <Keypad>
              {keypadKeys.map(([digit, letters]) => (
                <KeypadButton
                  key={digit}
                  aria-label={digit}
                  disabled={!canSendDtmf}
                  onClick={() => handleKeyPress(digit)}
                >
                  <span>{digit}</span>
                  <KeypadLetters aria-hidden="true">{letters}</KeypadLetters>
                </KeypadButton>
              ))}
            </Keypad>
          </KeypadPanel>
        </div>
        <Actions aria-label="Call controls" role="group">
          <CallAction>
            <SideControl
              id="call-dialog-record-btn"
              aria-label={getRecordButtonTitle(recording)}
              aria-pressed={recording}
              color="error"
              disabled={!canRecord}
              sx={{
                backgroundColor: recording ? 'error.main' : 'action.hover',
                color: recording ? 'error.contrastText' : 'error.main',
                '&:hover': { backgroundColor: recording ? 'error.dark' : 'action.selected' },
                '&.Mui-disabled': { backgroundColor: 'action.disabledBackground', color: 'action.disabled' },
              }}
              tooltip={canRecord ? getRecordButtonTitle(recording) : unavailTooltip}
              onClick={onRecordToggle}
            >
              {recording ? <Stop fontSize="small" /> : <FiberManualRecord fontSize="small" />}
            </SideControl>
            <ActionLabel variant="caption">{recording ? 'Stop' : 'Record'}</ActionLabel>
          </CallAction>
          <CallAction>
            <PrimaryCallButton
              id="call-dialog-call-btn"
              aria-label={callButtonTitle}
              disabled={isCallInProgress ? !canEnd : !canCall}
              sx={{
                backgroundColor: isCallInProgress ? 'error.main' : 'success.main',
                color: 'common.white',
                '&:hover': { backgroundColor: isCallInProgress ? 'error.dark' : 'success.dark' },
                '&.Mui-disabled': { backgroundColor: 'action.disabledBackground', color: 'action.disabled' },
              }}
              tooltip={callButtonTitle}
              onClick={isCallInProgress ? onHangup : onCall}
            >
              {isCallInProgress ? <CallEndButtonIcon /> : <CallButtonIcon />}
            </PrimaryCallButton>
            <ActionLabel variant="caption">{callButtonTitle}</ActionLabel>
          </CallAction>
          <CallAction>
            <SideControl
              aria-controls="call-dialog-keypad"
              aria-expanded={canSendDtmf && keypadOpen}
              aria-label="Keypad"
              disabled={!canSendDtmf}
              sx={{
                backgroundColor: keypadOpen ? 'primary.main' : 'action.hover',
                color: keypadOpen ? 'primary.contrastText' : 'text.secondary',
                '&:hover': { backgroundColor: keypadOpen ? 'primary.dark' : 'action.selected' },
              }}
              tooltip={canSendDtmf ? 'Keypad' : unavailTooltip}
              onClick={() => setKeypadOpen((open) => !open)}
            >
              <Dialpad fontSize="small" />
            </SideControl>
            <ActionLabel variant="caption">Keypad</ActionLabel>
          </CallAction>
        </Actions>
      </DialogContent>
    </FloatingCallBox>
  )
}

const getRecordButtonTitle = (isRecording: boolean) => (isRecording ? 'Stop recording' : 'Start recording')

const getCallButtonTitle = (callState: CallStateType) => {
  switch (callState) {
    case CallStateType.Initialized:
    case CallStateType.Ringing:
      return 'Cancel call'
    case CallStateType.Incall:
      return 'End call'
    case CallStateType.HangingUp:
      return 'Ending...'
    default:
      return 'Call'
  }
}

const FloatingCallBox = (props: PaperProps) => (
  <Draggable handle="#call-dialog-title" cancel={'.MuiDialogContent-root, .MuiIconButton-root'}>
    <FloatingCallPosition id="call-dialog-cont">
      <CallPaper {...props} />
    </FloatingCallPosition>
  </Draggable>
)

const FloatingCallPosition = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: 60,
  right: 60,
  width: 320,
  maxWidth: 'calc(100vw - 32px)',
  zIndex: 1499,
  [theme.breakpoints.down('sm')]: {
    bottom: 16,
    right: 16,
  },
}))

const CallPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'calc(100dvh - 120px)',
  [theme.breakpoints.down('sm')]: { maxHeight: 'calc(100dvh - 32px)' },
}))

const PrimaryCallButton = styled(ControlIconButton)({ width: 56, height: 56 })

const SideControl = styled(ControlIconButton)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  width: 40,
  height: 40,
  margin: '8px 0',
  '&:hover': { backgroundColor: theme.palette.action.selected },
}))

const StyledDialogTitle = styled(DialogTitle)({
  cursor: 'move',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: 8,
  minHeight: 55,
  padding: '10px 12px',
  backgroundColor: '#FAFAFA',
  '& h2': {
    fontSize: '16px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
})

const StyledShortenedLabel = styled(Typography)({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  minWidth: 0,
  flex: 1,
})

const CallStatus = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '5px 0 10px',
})

const Actions = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 12,
  maxWidth: 224,
  margin: '0 auto',
  padding: '16px 0 4px',
})

const CallAction = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
})

const ActionLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  minHeight: 32,
  textAlign: 'center',
}))

const KeypadPanel = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.primary.main}`,
  padding: theme.spacing(1, 0, 2),
  textAlign: 'center',
}))

const Keypad = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 56px)',
  gap: '8px 16px',
  justifyContent: 'center',
  marginTop: 12,
})

const KeypadDisplay = styled('output')(({ theme }) => ({
  display: 'block',
  fontFamily: 'monospace',
  fontSize: 22,
  letterSpacing: '0.08em',
  margin: '4px auto 0',
  overflowX: 'auto',
  padding: theme.spacing(1, 1.5),
  textAlign: 'center',
  whiteSpace: 'pre',
  width: 200,
}))

const KeypadButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: '50%',
  color: theme.palette.text.primary,
  display: 'flex',
  flexDirection: 'column',
  fontSize: 22,
  fontWeight: 400,
  height: 56,
  lineHeight: 1.2,
  minWidth: 56,
  padding: 0,
  width: 56,
  '&:hover': { backgroundColor: theme.palette.action.selected },
  '&:active': { backgroundColor: theme.palette.action.focus },
}))

const KeypadLetters = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 9,
  letterSpacing: '0.12em',
  lineHeight: 1.5,
  minHeight: '1.5em',
}))

const RecordingIndicator = styled('div')({
  color: '#FF6B6B',
  fontSize: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  animation: 'pulse 1.5s infinite',
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
})

export default CallDialogUI
